import { Auction, Item } from '@/api';
import BidTimer from '@/components/general/BidTimer';
import Price from '@/components/general/Price';
import AuctionPricePromotion from '@/contexts/AuctionPricePromotion';
import useAuth from '@/contexts/AuthContext';
import { setUpCalendarReminder } from '@/helpers/Calendar';
import clsx from 'clsx';
import i18next from 'i18n';
import Link from 'next/link';

const ProductTileBidSection = ({
    auction,
    hasAuction,
    comingSoon,
    auctionOver,
    item,
    endTime,
}: {
    auction: Omit<Auction, 'item'> | undefined;
    hasAuction: boolean;
    endTime?: Date;
    comingSoon: { startTime: Date } | undefined;
    auctionOver: boolean;
    item: Item;
}) => {
    const { user } = useAuth();

    const renderTile = (label: string, price?: string | null, timer?: Date | null, extra?: React.ReactNode) => (
        <Link prefetch={false} className="no-hover" href={`/item/${item.slug}`}>
            <div className="flex flex-col">
                <span className="text-grey15 text-[12px] font-[500] font-secondary tracking-[-0.024px]">
                    {label}
                </span>
                <div className="flex flex-row items-center justify-between text-[16px] font-[500] tracking-[-0.2px]">
                    {price && (
                        <Price
                            className="text-[16px] tracking-[-0.2px] font-[500] font-secondary"
                            justification="L"
                            number={price ?? '0'}
                        />
                    )}
                    {timer && <span className="mt-[-4px] text-green3"><BidTimer dueDate={timer} /></span>}
                    {extra}
                </div>
            </div>
        </Link>
    );

    if (auction && comingSoon) {
        return renderTile(
            i18next.t('common:product-tile.starts-soon'),
            undefined,
            comingSoon.startTime,
            <span
                className="text-[16px] font-[500] cursor-pointer text-black hover:underline mt-[-4px]"
                onClick={() => setUpCalendarReminder(auction, item, user)}
            >
                {i18next.t('common:button-text.remind-me')}
            </span>
        );
    }

    if (item.check_offers_possible && item.last_sold_amount && item.last_sold_amount > '0.00') {
        const label = item.auto_accept_offer && item.min_offer_amount && item.min_offer_amount > '0.00'
            ? i18next.t('common:product-tile.buy-now')
            : i18next.t('common:product-tile.last-sold');

        const price = item.auto_accept_offer ? item.min_offer_amount : item.last_sold_amount;

        return renderTile(label, price);
    }

    if (auction?.type === Auction.TypeEnum.Auction) {
        if (hasAuction && !auctionOver && !comingSoon) {
            return renderTile(i18next.t('item-text.current-bid-text.current-bid'), auction?.current_amount ?? '0', endTime);
        }
        if (auction && hasAuction && auctionOver && !comingSoon) {
            return renderTile(
                i18next.t('common:product-tile.winning-bid'),
                auction?.current_amount ?? '0',
                null,
                <>{auction.amount_of_bids} {i18next.t('common:product-tile.bids')}</>
            );
        }
        return null;
    }

    if (auction?.type === Auction.TypeEnum.BuyNow) {
        const label = auctionOver ? i18next.t('common:product-tile.sold-out') : i18next.t('common:product-tile.buy-now');

        return renderTile(
            label,
            null,
            null,
            <AuctionPricePromotion auction={auction}>
                {({ isPromoted, amount, promotedAmount }) => (
                    <span className="flex gap-1">
                        <Price
                            className={clsx('text-[16px] tracking-[-0.2px] font-[500] font-secondary', {
                                'line-through text-red': isPromoted,
                            })}
                            justification="L"
                            number={amount}
                        />
                        {isPromoted && (
                            <Price
                                className="text-[16px] tracking-[-0.2px] font-[500] font-secondary"
                                justification="L"
                                number={promotedAmount}
                            />
                        )}
                    </span>
                )}
            </AuctionPricePromotion>
        );
    }

    return null;
};

export default ProductTileBidSection;
